import { Sync as SyncIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import CustomerRequestLocationService from "../../../infrastructure/api/customerRequestLocation";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import JalaliDatePicker from "../../components/shared/jalali-datepicker";
import { formatApiDate } from "../SLN/market-growth/utils/commissionValidation";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import PartnerLeadDetailDialog from "./components/PartnerLeadDetailDialog";
import PartnerLeadKpiCards from "./components/PartnerLeadKpiCards";
import PartnerLeadStatusChip from "./components/PartnerLeadStatusChip";
import {
  LEAD_STATUS_OPTIONS,
  LOAN_STATUS_OPTIONS,
  MEHRSAN_STATUS_OPTIONS,
  PROJECT_STATUS_OPTIONS,
} from "./constants/leadEnums";
import { formatPartnerLeadMobile, isPartnerLevel1 } from "./utils/portalHelpers";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const DEFAULT_FILTERS = {
  status: "",
  projectStatus: "",
  mehrsanStatus: "",
  loanStatus: "",
  sourcePartnerId: "",
  provinceId: "",
  cityId: "",
  fromDate: null,
  toDate: null,
  hasProject: false,
  hasCommission: false,
};

const PartnerLeadsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [detailLead, setDetailLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const isLevel1 = pageData?.isLevel1Manager ?? isPartnerLevel1(profile);

  const subtitle = isLevel1
    ? "وضعیت ثبت‌نام، درخواست نیروگاه و پیشرفت پروژه‌های معرفی‌شده توسط شما و زیرمجموعه‌هایتان را مشاهده کنید."
    : "وضعیت لیدها و پروژه‌های معرفی‌شده توسط شما را مشاهده کنید.";

  const filteredCities = useMemo(() => {
    if (!filters.provinceId) return cities;
    return cities.filter(
      (c) => String(c.provinceId) === String(filters.provinceId)
    );
  }, [cities, filters.provinceId]);

  useEffect(() => {
    CustomerRequestLocationService.loadCustomerRequestLocationLists().then(({ provinces: p, cities: c }) => {
      setProvinces(p || []);
      setCities(c || []);
    });
  }, []);

  const buildParams = useCallback(() => {
    const params = { page: page + 1, pageSize };
    if (filters.status) params.status = filters.status;
    if (filters.projectStatus) params.projectStatus = filters.projectStatus;
    if (filters.mehrsanStatus) params.mehrsanStatus = filters.mehrsanStatus;
    if (filters.loanStatus) params.loanStatus = filters.loanStatus;
    if (filters.sourcePartnerId) params.sourcePartnerId = filters.sourcePartnerId;
    if (filters.provinceId) params.provinceId = filters.provinceId;
    if (filters.cityId) params.cityId = filters.cityId;
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    if (filters.hasProject) params.hasProject = true;
    if (filters.hasCommission) params.hasCommission = true;
    return params;
  }, [filters, page, pageSize]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const [leadsRes, profileRes] = await Promise.all([
        PartnerCommissionApi.getLeads(buildParams()),
        PartnerCommissionApi.getProfile(),
      ]);

      const leadsNorm = PartnerCommissionApi.normalize(
        leadsRes,
        "بارگذاری لیدها ناموفق بود."
      );
      const profileNorm = PartnerCommissionApi.normalize(profileRes);

      if (leadsNorm.noPartnerProfile || profileNorm.noPartnerProfile) {
        setNoPartnerProfile(true);
        setPageData(null);
        setProfile(null);
        return;
      }

      setProfile(profileNorm.data || null);

      if (leadsNorm.error) {
        setError(leadsNorm.error);
        setPageData(null);
        return;
      }

      setPageData(leadsNorm.data || null);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const openDetail = async (leadCode) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetailLead(null);
    try {
      const res = await PartnerCommissionApi.getLeadDetail(leadCode);
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری جزئیات لید ناموفق بود."
      );
      if (normalized.data) setDetailLead(normalized.data);
    } finally {
      setDetailLoading(false);
    }
  };

  const items = Array.isArray(pageData?.items) ? pageData.items : [];
  const sourceOptions = Array.isArray(pageData?.sourcePartnerOptions)
    ? pageData.sourcePartnerOptions
    : [];
  const totalCount = pageData?.totalCount ?? 0;

  const applyFilters = () => {
    setPage(0);
    loadLeads();
  };

  return (
    <PartnerCommissionPageShell
      title="گزارش لیدهای معرفی‌شده"
      description={subtitle}
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={loadLeads}
      empty={false}
    >
      <Tooltip
        title={
          isLevel1
            ? "مدیر سطح یک شماره موبایل کامل را می‌بیند؛ زیرمجموعه‌ها فقط نسخه ماسک‌شده را مشاهده می‌کنند."
            : "اطلاعات تماس مشتریان به‌صورت محافظت‌شده نمایش داده می‌شود."
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          {isLevel1
            ? "شماره موبایل متقاضیان به‌صورت کامل نمایش داده می‌شود."
            : "اطلاعات تماس مشتریان به‌صورت محافظت‌شده نمایش داده می‌شود."}
        </Alert>
      </Tooltip>

      {isLevel1 ? (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            component={RouterLink}
            to="/SLN/Partner/PartnerRegisteredWithoutProject"
            variant="outlined"
            size="small"
          >
            گزارش ثبت‌نام بدون درخواست
          </Button>
        </Box>
      ) : null}

      <PartnerLeadKpiCards summary={pageData?.summary} />

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت لید</InputLabel>
                <Select
                  label="وضعیت لید"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  {LEAD_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value || "all-lead"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت درخواست نیروگاه</InputLabel>
                <Select
                  label="وضعیت درخواست نیروگاه"
                  value={filters.projectStatus}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, projectStatus: e.target.value }))
                  }
                >
                  {PROJECT_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value || "all-project"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت مهرسان</InputLabel>
                <Select
                  label="وضعیت مهرسان"
                  value={filters.mehrsanStatus}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, mehrsanStatus: e.target.value }))
                  }
                >
                  {MEHRSAN_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value || "all-mehrsan"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت وام</InputLabel>
                <Select
                  label="وضعیت وام"
                  value={filters.loanStatus}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, loanStatus: e.target.value }))
                  }
                >
                  {LOAN_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value || "all-loan"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isLevel1 ? (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>نماینده معرف</InputLabel>
                  <Select
                    label="نماینده معرف"
                    value={filters.sourcePartnerId}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        sourcePartnerId: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {sourceOptions.map((o) => (
                      <MenuItem key={o.partnerId} value={String(o.partnerId)}>
                        {o.displayName}
                        {o.partnerCode ? ` (${o.partnerCode})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>استان</InputLabel>
                <Select
                  label="استان"
                  value={filters.provinceId}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      provinceId: e.target.value,
                      cityId: "",
                    }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {provinces.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.provinceName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>شهر</InputLabel>
                <Select
                  label="شهر"
                  value={filters.cityId}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, cityId: e.target.value }))
                  }
                  disabled={!filters.provinceId && filteredCities.length > 200}
                >
                  <MenuItem value="">همه</MenuItem>
                  {filteredCities.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.cityName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(v) => setFilters((f) => ({ ...f, fromDate: v }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(v) => setFilters((f) => ({ ...f, toDate: v }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.hasProject}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        hasProject: e.target.checked,
                      }))
                    }
                  />
                }
                label="فقط لیدهای دارای درخواست"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.hasCommission}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        hasCommission: e.target.checked,
                      }))
                    }
                  />
                }
                label="فقط لیدهای دارای پورسانت"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="contained" onClick={applyFilters} fullWidth>
                اعمال فیلتر
              </Button>
            </Grid>
            <Grid item>
              <Tooltip title="بارگذاری مجدد">
                <IconButton onClick={loadLeads}>
                  <SyncIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ overflowX: "auto", p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>کد لید</TableCell>
                <TableCell>شماره پیگیری</TableCell>
                <TableCell>موبایل</TableCell>
                <TableCell>شهر / استان</TableCell>
                {isLevel1 ? <TableCell>نماینده معرف</TableCell> : null}
                <TableCell>تاریخ ثبت‌نام</TableCell>
                <TableCell>وضعیت لید</TableCell>
                <TableCell>وضعیت درخواست</TableCell>
                <TableCell>مهرسان</TableCell>
                <TableCell>وام</TableCell>
                <TableCell>پورسانت</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isLevel1 ? 12 : 11}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography color="text.secondary">
                      هنوز لیدی از طریق لینک معرفی شما ثبت نشده است.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {items.map((row) => (
                <TableRow key={row.leadCode} hover>
                  <TableCell>{row.leadCode}</TableCell>
                  <TableCell dir="ltr" sx={{ textAlign: "right" }}>
                    {row.referenceId ?? row.ReferenceId ?? "—"}
                  </TableCell>
                  <TableCell dir="ltr" sx={{ textAlign: "right" }}>
                    {formatPartnerLeadMobile(row, isLevel1)}
                  </TableCell>
                  <TableCell>
                    {[row.cityName, row.provinceName].filter(Boolean).join(" / ") ||
                      "—"}
                  </TableCell>
                  {isLevel1 ? (
                    <TableCell>{row.sourcePartnerDisplayName || "—"}</TableCell>
                  ) : null}
                  <TableCell>{formatDate(row.registeredAt)}</TableCell>
                  <TableCell>
                    <PartnerLeadStatusChip
                      label={row.leadStatus}
                      statusKey={row.leadStatusKey}
                    />
                  </TableCell>
                  <TableCell>
                    <PartnerLeadStatusChip
                      label={row.projectStatus}
                      statusKey={row.projectStatusKey}
                    />
                  </TableCell>
                  <TableCell>
                    <PartnerLeadStatusChip
                      label={row.mehrsanStatus}
                      statusKey={row.mehrsanStatusKey}
                    />
                  </TableCell>
                  <TableCell>
                    <PartnerLeadStatusChip
                      label={row.loanStatus}
                      statusKey={row.loanStatusKey}
                    />
                  </TableCell>
                  <TableCell>
                    <PartnerLeadStatusChip
                      label={row.commissionStatus}
                      statusKey={row.commissionStatusKey}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => openDetail(row.leadCode)}
                      aria-label="مشاهده جزئیات"
                      disabled={row.canViewDetail === false}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="تعداد در صفحه"
          />
        </CardContent>
      </Card>

      <PartnerLeadDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        lead={detailLead}
        loading={detailLoading}
        isLevel1={isLevel1}
      />
    </PartnerCommissionPageShell>
  );
};

export default PartnerLeadsPage;
